/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { NotFound } from '../../../../routes/not-found/NotFound';
import { useHistory, useParams } from 'react-router-dom';
import { useGetSurveysQuery } from '../../api/materiality-assessment';
import { BlockingLoader } from '@g17eco/atoms/loader';
import { AssessmentInsights } from '../../components/assessment-insights/AssessmentInsights';
import { useAppSelector } from '@reducers/index';
import { currentInitiative } from '@selectors/initiative';
import { canAccessInsightsAndDownloads } from '@selectors/user';
import NotAuthorised from '@routes/not-authorised';
import { AssessmentInsightsPlaceholder } from '@apps/materiality-tracker/components/assessment-insights/AssessmentInsightsPlaceholder';
import type { OnChangeValue } from 'react-select';
import type { Option } from '@g17eco/molecules/select/types';
import { generateUrl } from '@routes/util';
import { ROUTES } from '@constants/routes';
import { UnlockAssessmentAlert } from '@apps/materiality-tracker/components/assessment/UnlockAssessmentAlert';

export const AssessmentInsightRoute = () => {
  const { initiativeId, insightSurveyId } = useParams<{
    initiativeId: string;
    insightSurveyId: string;
  }>();

  const history = useHistory();
  const initiative = useAppSelector(currentInitiative);
  const { data: surveys, isFetching: isGettingSurveys } = useGetSurveysQuery({ initiativeId });
  const canAccessDownloadsByRole = useAppSelector((state) => canAccessInsightsAndDownloads(state, initiativeId));

  if (!canAccessDownloadsByRole) {
    return <NotAuthorised />;
  }

  if (isGettingSurveys || !initiative) {
    return <BlockingLoader />;
  }

  const survey = surveys?.find((s) => s._id === insightSurveyId);
  if (!surveys?.length || !survey) {
    return <NotFound />;
  }

  const onClickItem = (option: OnChangeValue<Option<string>, false>) => {
    if (option?.value === survey._id) {
      return;
    }
    history.push(
      generateUrl(ROUTES.MATERIALITY_TRACKER_INSIGHTS, {
        initiativeId: initiative._id,
        insightSurveyId: option?.value,
      }),
    );
  };

  return (
    <div>
      <UnlockAssessmentAlert survey={survey} />
      <AssessmentInsightsPlaceholder
        key={survey._id}
        selectedSurvey={survey}
        surveys={surveys}
        onClickItem={onClickItem}
      >
        <AssessmentInsights
          selectedSurvey={survey}
          surveys={surveys}
          initiative={initiative}
          onClickItem={onClickItem}
        />
      </AssessmentInsightsPlaceholder>
    </div>
  );
};
